const GIS_SCRIPT_URL = 'https://accounts.google.com/gsi/client';

interface GsiCodeClient {
  requestCode: () => void;
}

interface GsiCodeClientConfig {
  client_id: string;
  scope: string;
  ux_mode: 'popup' | 'redirect';
  callback: (resp: { code?: string; error?: string }) => void;
  error_callback?: (err: { type?: string; message?: string }) => void;
}

interface GoogleGlobal {
  accounts?: {
    oauth2?: {
      initCodeClient: (config: GsiCodeClientConfig) => GsiCodeClient;
    };
  };
}

declare global {
  var google: GoogleGlobal | undefined;
}

let loaderPromise: Promise<void> | null = null;

function loadGsiScript(): Promise<void> {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Google Identity Services requires a browser context'));
  }
  if (window.google?.accounts?.oauth2) return Promise.resolve();
  if (loaderPromise) return loaderPromise;

  loaderPromise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${GIS_SCRIPT_URL}"]`);
    if (existing) {
      if (window.google?.accounts?.oauth2) return resolve();
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error('Failed to load GIS')), {
        once: true,
      });
      return;
    }
    const script = document.createElement('script');
    script.src = GIS_SCRIPT_URL;
    script.async = true;
    script.defer = true;
    script.addEventListener('load', () => resolve(), { once: true });
    script.addEventListener('error', () => reject(new Error('Failed to load GIS')), {
      once: true,
    });
    document.head.appendChild(script);
  });

  return loaderPromise;
}

export interface GoogleAuthCodeResult {
  code: string;
  redirectUri: string;
}

export async function requestGoogleAuthCode(input: {
  clientId: string;
  scopes: string[];
}): Promise<GoogleAuthCodeResult> {
  await loadGsiScript();
  const oauth2 = window.google?.accounts?.oauth2;
  if (!oauth2) throw new Error('Google Identity Services unavailable');

  return new Promise<GoogleAuthCodeResult>((resolve, reject) => {
    const client = oauth2.initCodeClient({
      client_id: input.clientId,
      scope: input.scopes.join(' '),
      ux_mode: 'popup',
      callback: (resp) => {
        if (resp.error) return reject(new Error(resp.error));
        if (!resp.code) return reject(new Error('Google Identity Services returned no code'));
        resolve({ code: resp.code, redirectUri: 'postmessage' });
      },
      error_callback: (err) => {
        if (err.type === 'popup_closed') return reject(new Error('cancelled'));
        reject(new Error(err.message ?? err.type ?? 'GIS error'));
      },
    });
    client.requestCode();
  });
}
